import type { ConfiguracionItem } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, ExternalLink, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { apiDocsUrl, getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { configuracionApi } from '../api/hardening.api';

export function ConfiguracionAdminPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<ConfiguracionItem | null>(null);
  const es = useI18n();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.admin.configuracion,
    queryFn: () => configuracionApi.list(),
  });

  if (isLoading) {
    return <p className="p-6 text-sm text-marca-gris/70">{es.configuracionAdmin.cargando}</p>;
  }
  if (error) {
    return (
      <div className="p-6">
        <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="font-titulos text-3xl font-bold text-text-marca">{es.configuracionAdmin.titulo}</h1>
      <p className="mt-1 text-sm text-marca-gris/80">{es.configuracionAdmin.descripcion}</p>

      {/* H-04 — Integración con otras apps */}
      <div className="mt-4 rounded-md border border-marca-neutro bg-surface p-4">
        <h2 className="text-sm font-semibold text-text-marca">{es.configuracionAdmin.integracion.titulo}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Link to="/admin/api-keys">
            <Button variant="outline" size="sm">
              <KeyRound size={14} />
              {es.configuracionAdmin.integracion.apiKeys}
            </Button>
          </Link>
          <a href={apiDocsUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink size={14} />
              {es.configuracionAdmin.integracion.docs}
            </Button>
          </a>
          <span className="text-xs text-marca-gris/60">{es.configuracionAdmin.integracion.apiKeysDesc}</span>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-marca-neutro bg-surface">
        <table className="w-full text-xs">
          <thead className="border-b border-marca-neutro bg-marca-neutro/30 text-[10px] uppercase text-marca-gris/70">
            <tr>
              <th className="px-2 py-2 text-left">{es.configuracionAdmin.col.clave}</th>
              <th className="px-2 py-2 text-left">{es.configuracionAdmin.col.valor}</th>
              <th className="px-2 py-2 text-left">{es.configuracionAdmin.col.descripcion}</th>
              <th className="px-2 py-2 text-left">{es.configuracionAdmin.col.actualizadoEn}</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-2 py-6 text-center text-marca-gris/60">
                  {es.configuracionAdmin.vacio}
                </td>
              </tr>
            ) : (
              items.map((c) => (
                <tr key={c.clave} className="border-b border-marca-neutro/40">
                  <td className="px-2 py-1 font-mono">{c.clave}</td>
                  <td className="px-2 py-1 font-mono text-marca-gris/80">{JSON.stringify(c.valor)}</td>
                  <td className="px-2 py-1 text-marca-gris/70">{c.descripcion ?? '—'}</td>
                  <td className="px-2 py-1 text-[10px] text-marca-gris/60">
                    {new Date(c.actualizadoEn).toLocaleString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires',
                    })}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditando(c)}
                      aria-label={es.configuracionAdmin.editar}
                    >
                      <Edit2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editando && (
        <ConfiguracionEditarDialog
          item={editando}
          open
          onOpenChange={(o) => !o && setEditando(null)}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: qk.admin.configuracion });
            setEditando(null);
          }}
        />
      )}
    </div>
  );
}

function ConfiguracionEditarDialog({
  item,
  open,
  onOpenChange,
  onSuccess,
}: {
  item: ConfiguracionItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}): JSX.Element {
  const [raw, setRaw] = useState(JSON.stringify(item.valor, null, 2));
  const [error, setError] = useState<string | null>(null);
  const es = useI18n();

  const mutation = useMutation({
    mutationFn: () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        throw new Error('INVALID_JSON');
      }
      return configuracionApi.actualizar(item.clave, { valor: parsed });
    },
    onSuccess: () => {
      toast.success(es.configuracionAdmin.exito);
      onSuccess();
    },
    onError: (e) => {
      if (e.message === 'INVALID_JSON') setError(es.configuracionAdmin.invalidJson);
      else setError(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{item.clave}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {item.descripcion && <p className="text-xs text-marca-gris/70">{item.descripcion}</p>}
          {error && <Alert variant="error">{error}</Alert>}
          <FormField label={es.configuracionAdmin.col.valor} htmlFor="c-valor">
            <Textarea
              id="c-valor"
              rows={6}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="font-mono text-xs"
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {es.configuracionAdmin.cancelar}
          </Button>
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {es.configuracionAdmin.guardar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
